import React, { memo } from 'react';
import PropTypes from 'prop-types';
import styled, { keyframes } from 'styled-components';
import { Card, Tooltip } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';

/**
 * ⚡ PERFORMANCE OPTIMIZATION: Memoized StatCard with skeleton loading
 */

// The shadow carries the lift (the accent gradient hairline is retired); the
// border never goes accent on hover.
const StyledCard = styled(Card)`
  border-radius: ${({ theme }) => theme.borderRadius.card};
  transition: all 0.3s ease;
  height: 100%;
  overflow: hidden;
  position: relative;

  &:hover {
    box-shadow: ${({ theme }) => theme.shadows.lg};
  }

  .ant-card-body {
    padding: 24px;
    position: relative;
  }
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`;

const IconWrapper = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: ${({ $color }) => `color-mix(in srgb, ${$color} 12%, transparent)`};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  color: ${({ $color }) => $color};
`;

const InfoIcon = styled(InfoCircleOutlined)`
  color: ${({ theme }) => theme.colors.textMuted};
  cursor: pointer;

  &:hover {
    color: ${({ theme }) => theme.colors.text};
  }
`;

// Value in Lora with tabular figures; label calm Inter secondary; trend Inter 12
const StatValue = styled.div`
  font-family: ${({ theme }) => theme.typography.fontFamily.display};
  font-size: 34px;
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.ink};
  line-height: 1.1;
  letter-spacing: -0.02em;
  font-variant-numeric: tabular-nums lining-nums;
  margin-top: 6px;
`;

const StatLabel = styled.div`
  font-size: 13.5px;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-top: 6px;
`;

const TrendIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
  font-size: 12px;
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ $positive, theme }) => $positive ? theme.colors.success : theme.colors.error};
  margin-top: 12px;
`;

// ⚡ Skeleton animation for smooth loading state
const shimmer = keyframes`
  0% {
    background-position: -200px 0;
  }
  100% {
    background-position: calc(200px + 100%) 0;
  }
`;

const SkeletonBase = styled.div`
  background: linear-gradient(
    90deg,
    ${({ theme }) => theme.colors.secondary} 0%,
    ${({ theme }) => theme.colors.surface} 50%,
    ${({ theme }) => theme.colors.secondary} 100%
  );
  background-size: 200px 100%;
  animation: ${shimmer} 1.5s ease-in-out infinite;
`;

const SkeletonCircle = styled(SkeletonBase)`
  width: 40px;
  height: 40px;
  border-radius: 10px;
`;

const SkeletonText = styled(SkeletonBase)`
  height: ${({ $height }) => $height || '20px'};
  width: ${({ $width }) => $width || '100%'};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  margin-bottom: ${({ theme }) => theme.spacing.xs};
`;

const LoadingContent = styled.div`
  min-height: 80px;
`;

/**
 * ⚡ Memoized StatCard component - prevents unnecessary re-renders
 */
const StatCard = memo(({
  icon,
  value,
  label,
  color = 'var(--odin-accent)', // any colour token works: IconWrapper tints it with color-mix()
  trend,
  trendLabel,
  tooltip,
  loading = false,
  onClick,
  className,
}) => {
  const IconComponent = icon;

  return (
    <StyledCard className={className} onClick={onClick} hoverable={!!onClick}>
      {loading ? (
        <LoadingContent>
          <CardHeader>
            <SkeletonCircle />
          </CardHeader>
          <SkeletonText $width="60%" $height="32px" />
          <SkeletonText $width="80%" $height="14px" />
        </LoadingContent>
      ) : (
        <>
          <CardHeader>
            <IconWrapper $color={color}>
              {IconComponent && <IconComponent />}
            </IconWrapper>
            {tooltip && (
              <Tooltip title={tooltip}>
                <InfoIcon />
              </Tooltip>
            )}
          </CardHeader>
          <StatValue>{value ?? '--'}</StatValue>
          <StatLabel>{label}</StatLabel>
          {trend !== undefined && (
            <TrendIndicator $positive={trend >= 0}>
              {trend >= 0 ? '↑' : '↓'} {Math.abs(trend)}%
              {trendLabel && <span>vs {trendLabel}</span>}
            </TrendIndicator>
          )}
        </>
      )}
    </StyledCard>
  );
});

StatCard.displayName = 'StatCard';

StatCard.propTypes = {
  icon: PropTypes.elementType,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  label: PropTypes.string.isRequired,
  color: PropTypes.string,
  trend: PropTypes.number,
  trendLabel: PropTypes.string,
  tooltip: PropTypes.string,
  loading: PropTypes.bool,
  onClick: PropTypes.func,
  className: PropTypes.string,
};

export default StatCard;
